// components/LiveStatsSection.jsx
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';

const LiveStatsSection = () => {
  const [counts, setCounts] = useState({
    students: 0,
    courses: 0,
    instructors: 0,
    companies: 0
  });

  const finalCounts = {
    students: 15847,
    courses: 450,
    instructors: 125,
    companies: 850
  };

  useEffect(() => {
    const duration = 2000; // 2 seconds
    const steps = 60;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      setCounts({
        students: Math.floor(finalCounts.students * progress),
        courses: Math.floor(finalCounts.courses * progress),
        instructors: Math.floor(finalCounts.instructors * progress),
        companies: Math.floor(finalCounts.companies * progress)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setCounts(finalCounts);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, []);

  const stats = [
    {
      label: "Active Students",
      value: counts.students.toLocaleString(),
      icon: "Users",
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/20",
      ringColor: "ring-emerald-500/30"
    },
    {
      label: "Expert Courses",
      value: counts.courses.toLocaleString(),
      icon: "BookOpen",
      color: "text-green-400",
      bgColor: "bg-green-400/20",
      ringColor: "ring-green-400/30"
    },
    {
      label: "Industry Experts",
      value: counts.instructors.toLocaleString(),
      icon: "Award",
      color: "text-lime-400",
      bgColor: "bg-lime-400/20",
      ringColor: "ring-lime-400/30"
    },
    {
      label: "Partner Companies",
      value: counts.companies.toLocaleString(),
      icon: "Building",
      color: "text-teal-400",
      bgColor: "bg-teal-400/20",
      ringColor: "ring-teal-400/30"
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-emerald-50 to-green-50 text-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.1,
                type: "spring",
                stiffness: 100,
                damping: 15
              }}
              whileHover={{ 
                y: -8,
                transition: { duration: 0.3 }
              }}
              className="text-center group"
            >
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ 
                  duration: 0.5, 
                  delay: index * 0.1 + 0.2,
                  type: "spring",
                  stiffness: 150
                }}
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${stat.bgColor} ${stat.ringColor} ring-4 mb-6 group-hover:ring-8 transition-all duration-300`}
              >
                <motion.div
                  initial={{ rotate: -180, opacity: 0 }}
                  whileInView={{ rotate: 0, opacity: 1 }}
                  transition={{ 
                    duration: 0.7, 
                    delay: index * 0.1 + 0.3,
                    type: "spring",
                    stiffness: 120
                  }}
                >
                  <Icon name={stat.icon} size={28} className={`${stat.color} drop-shadow-lg`} />
                </motion.div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ 
                  duration: 0.5, 
                  delay: index * 0.1 + 0.4 
                }}
                className="space-y-2"
              >
                <div className={`text-4xl lg:text-5xl font-bold ${stat.color} drop-shadow-sm`}>
                  {stat.value}
                </div>
                <div className="text-gray-600 text-sm md:text-base font-medium tracking-wide">
                  {stat.label}
                </div>
              </motion.div>
              
              {/* Animated underline effect */}
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: "60%" }}
                transition={{ 
                  duration: 0.8, 
                  delay: index * 0.1 + 0.5,
                  ease: "easeOut"
                }}
                className={`h-1 ${stat.bgColor} rounded-full mx-auto mt-4 group-hover:w-3/4 transition-all duration-300`}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LiveStatsSection;